// src/components/booking/GuestVehicleForm.js
import React, { useState, useEffect } from 'react';
import { Car, Mail, Info } from 'lucide-react';
import { inferVehicleType } from '../../utils/vehiclePricing';
import { ref, push, set } from 'firebase/database';
import { database } from '../../firebase';
import emailService from '../../services/emailService';

const GuestVehicleForm = ({ onVehicleSubmit }) => {
  const [vehicleInfo, setVehicleInfo] = useState({
    make: '',
    model: '',
    year: '',
    color: '',
    type: ''
  });
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [inferredType, setInferredType] = useState(null);
  const [showTypeInfo, setShowTypeInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Try to infer vehicle type when make/model changes
    if (vehicleInfo.make && vehicleInfo.model) {
      const type = inferVehicleType({
        make: vehicleInfo.make,
        model: vehicleInfo.model
      });
      
      if (type && type !== vehicleInfo.type) {
        setInferredType(type);
      } else {
        setInferredType(null);
      }
    } else {
      setInferredType(null);
    }
  }, [vehicleInfo.make, vehicleInfo.model, vehicleInfo.type]);

  const saveToLocalStorage = (guestVehicle) => {
    try {
      console.log("Saving guest vehicle to localStorage:", guestVehicle);
      
      // Generate a unique ID if not already present
      const vehicleId = guestVehicle.id || 'guest-vehicle-' + Date.now();
      
      // Add metadata if not already present
      const vehicleWithMeta = {
        ...guestVehicle,
        id: vehicleId,
        createdAt: guestVehicle.createdAt || new Date().toISOString()
      };
      
      // Save to localStorage
      localStorage.setItem('guestVehicle', JSON.stringify(vehicleWithMeta));
      
      // Store email separately for easy access
      if (email) {
        localStorage.setItem('guestEmail', email);
      }
      
      // Store phone if available
      if (phoneNumber) {
        localStorage.setItem('guestPhone', phoneNumber);
      }
      
      console.log("Guest vehicle saved successfully to localStorage with ID:", vehicleId);
      
      return vehicleWithMeta;
    } catch (error) {
      console.error("Error saving guest vehicle to localStorage:", error);
      throw error;
    }
  };
  
  const saveToDatabase = async (guestVehicle) => {
    try {
      console.log("Saving guest vehicle to database:", guestVehicle);
      
      // Use the correct path that matches your security rules: guestVehicles
      const guestVehiclesRef = ref(database, 'guestVehicles');
      
      // Create a new entry with auto-generated ID
      const newVehicleRef = push(guestVehiclesRef);
      const vehicleId = newVehicleRef.key;
      
      // Update the vehicle object with the Firebase ID
      const vehicleWithId = {
        ...guestVehicle,
        id: vehicleId
      };
      
      // Save to Firebase
      await set(newVehicleRef, vehicleWithId);
      
      console.log("Guest vehicle saved successfully to database with ID:", vehicleId);
      
      return vehicleWithId;
    } catch (error) {
      console.error("Error saving guest vehicle to database:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      // Validate required fields
      if (!vehicleInfo.make || !vehicleInfo.model || !vehicleInfo.year || !email) {
        setError("Please fill in all required fields");
        setIsSubmitting(false);
        return;
      }
      
      // Use the inferred type if available and user hasn't selected one
      const finalVehicleInfo = {
        ...vehicleInfo,
        type: vehicleInfo.type || inferredType || ''
      };
      
      console.log("Submitting guest vehicle form with data:", {
        ...finalVehicleInfo,
        contactEmail: email
      });
      
      // Create the vehicle object with contact info
      const guestVehicle = {
        ...finalVehicleInfo,
        contactEmail: email,
        contactPhone: phoneNumber,
        location: "", // Add empty location field
        createdAt: new Date().toISOString()
      };
      
      // First save to database (Firebase)
      let savedVehicle;
      try {
        savedVehicle = await saveToDatabase(guestVehicle);
      } catch (dbError) {
        console.error("Database save failed, falling back to localStorage:", dbError);
        // If database save fails, continue with localStorage only
        savedVehicle = guestVehicle;
      }
      
      // Then save to localStorage (as backup and for easy access)
      const localSavedVehicle = saveToLocalStorage(savedVehicle);

      // Send form data to bookings@jp-autodetailing.com to trigger auto-responder
      try {
        const formData = {
          name: `Guest User`, // Default name for guest users
          email: email,
          phone: phoneNumber,
          vehicleYear: finalVehicleInfo.year,
          vehicleMake: finalVehicleInfo.make,
          vehicleModel: finalVehicleInfo.model,
          vehicleColor: finalVehicleInfo.color,
          location: '', // This will be filled in later in the booking process
          instructions: '' // This will be filled in later in the booking process
        };

        await emailService.sendFormToBookings('guest-vehicle', formData, 'booking-vehicle-form');
        console.log('Guest vehicle form sent to bookings email successfully');
      } catch (emailError) {
        console.error('Failed to send vehicle form to bookings email:', emailError);
        // Don't fail the submission if email fails
      }

      // Pass the vehicle data back to the parent component
      onVehicleSubmit(localSavedVehicle);

      // Debug logging
      console.log("Guest vehicle form submitted and saved successfully");
      
    } catch (err) {
      console.error("Error in form submission:", err);
      setError("Failed to save vehicle information. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setVehicleInfo({
      ...vehicleInfo,
      [name]: value
    });
  };
  
  // Apply the inferred type
  const handleApplyInferredType = () => {
    if (inferredType) {
      setVehicleInfo({
        ...vehicleInfo,
        type: inferredType
      });
      setInferredType(null);
    }
  };

  // Get display name for vehicle type
  const getVehicleTypeDisplay = (type) => {
    const displayNames = {
      'sedan': 'Sedan',
      'suv': 'SUV',
      'truck': 'Truck',
      'van': 'Van/Minivan',
      'coupe': 'Coupe/Sports Car',
      'convertible': 'Convertible',
      'hatchback': 'Hatchback',
      'wagon': 'Wagon',
      'compact': 'Compact Car',
      'large_suv': 'Large SUV'
    };
    
    return displayNames[type] || type;
  };

  return (
    <div className="border rounded-lg p-5 border-blue-200 bg-blue-50">
      <h4 className="font-medium mb-3 flex items-center">
        <Car className="h-4 w-4 mr-2" />
        Enter Vehicle Details
      </h4>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Make *
            </label>
            <input
              type="text"
              name="make"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="e.g. Toyota"
              required
              value={vehicleInfo.make}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Model *
            </label>
            <input
              type="text"
              name="model"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="e.g. Camry"
              required
              value={vehicleInfo.model}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Year *
            </label>
            <input
              type="text"
              name="year"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="e.g. 2021"
              required
              value={vehicleInfo.year}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Color
            </label>
            <input
              type="text"
              name="color"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="e.g. Blue"
              value={vehicleInfo.color}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-1 flex justify-between">
            <span>Vehicle Type</span>
            <button 
              type="button"
              className="text-blue-600 text-xs flex items-center"
              onClick={() => setShowTypeInfo(!showTypeInfo)}
            >
              <Info className="h-3 w-3 mr-1" />
              Why we ask this
            </button>
          </label>
          
          {showTypeInfo && (
            <div className="mb-2 p-2 bg-blue-100 rounded-md text-xs text-gray-700">
              Your vehicle type helps us determine the appropriate pricing and 
              ensure we allocate enough time for your service. Different vehicle sizes 
              require different amounts of cleaning products and labor.
            </div>
          )}
          
          {/* Show inferred type suggestion if available */}
          {inferredType && !vehicleInfo.type && (
            <div className="mb-2 p-2 border border-blue-300 bg-blue-50 rounded text-sm flex justify-between items-center">
              <span>
                Based on your vehicle, we think it's a <strong>{getVehicleTypeDisplay(inferredType)}</strong>
              </span>
              <button
                type="button"
                className="text-blue-600 text-xs"
                onClick={handleApplyInferredType}
              >
                Use this
              </button>
            </div>
          )}
          
          <select 
            name="type"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            value={vehicleInfo.type}
            onChange={handleChange}
          >
            <option value="">Select vehicle type</option>
            <option value="sedan">Sedan</option>
            <option value="suv">SUV</option>
            <option value="truck">Truck</option>
            <option value="van">Van/Minivan</option>
            <option value="coupe">Coupe/Sports Car</option>
            <option value="convertible">Convertible</option>
            <option value="hatchback">Hatchback</option>
            <option value="wagon">Wagon</option>
            <option value="other">Other</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            This helps us accurately price our service for your vehicle size
          </p>
        </div>

        <div className="border-t border-gray-200 pt-3 mt-3 mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
            <Mail className="h-3 w-3 mr-1" />
            Email Address *
          </label>
          <input
            type="email"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder="your@email.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            We'll send your booking confirmation and updates to this email
          </p>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Phone Number (optional)
          </label> 
          <input
            type="tel"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder="(123) 456-7890"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </div>
        
        <button 
          type="submit"
          className={`w-full ${
            isSubmitting 
              ? 'bg-blue-400 cursor-not-allowed' 
              : 'bg-blue-500 hover:bg-blue-600'
          } text-white py-2 px-4 rounded-md transition-colors font-medium`}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          ) : (
            'Continue with this vehicle'
          )}
        </button>
      </form>
      
      <p className="text-xs text-gray-500 mt-3">
        * Required information
      </p>
    </div>
  );
};

export default GuestVehicleForm;